// src/buildPortalTheme.ts
import { alpha as alpha2, createTheme as createTheme2, lighten } from "@mui/material/styles";
import { toMUITheme } from "@hopdrive/ui/theme";
import { allTokens } from "@hopdrive/ui/tokens";

// src/branding.ts
function resolveActiveBranding(branding) {
  return branding && branding.active === true ? branding : null;
}

// src/deriveColor.ts
import { alpha, createTheme } from "@mui/material/styles";
var HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
function isHex(value) {
  return typeof value === "string" && HEX.test(value.trim());
}
function deriveBrandPaletteColor(hex) {
  if (!isHex(hex)) return null;
  const { main, light, dark, contrastText } = createTheme().palette.augmentColor({ color: { main: hex.trim() } });
  return { main, light, dark, contrastText };
}
var LIGHT_INK = "#ffffff";
var DARK_INK = "#202430";
function contrastSlotsFromInk(ink) {
  return {
    navContrastText: ink,
    dividerContrast: alpha(ink, 0.09),
    text: {
      contrastPrimary: ink,
      contrastSecondary: alpha(ink, 0.63),
      contrastDisabled: alpha(ink, 0.45)
    },
    action: {
      contrastActive: alpha(ink, 0.5),
      contrastHover: alpha(ink, 0.05),
      contrastSelected: alpha(ink, 0.09),
      contrastDisabled: alpha(ink, 0.31),
      contrastDisabledBackground: alpha(ink, 0.14),
      contrastFocus: alpha(ink, 0.14)
    }
  };
}
function relativeLuminance(hex) {
  const h = hex.trim().replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.slice(0, 6);
  const channels = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
  const [r, g, b] = channels.map((v) => v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function contrastRatio(a, b) {
  const [hi, lo] = a >= b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}
function readableContrastSlots(hex) {
  if (!isHex(hex)) return null;
  const bg = relativeLuminance(hex);
  const ink = contrastRatio(bg, relativeLuminance(LIGHT_INK)) >= contrastRatio(bg, relativeLuminance(DARK_INK)) ? LIGHT_INK : DARK_INK;
  return contrastSlotsFromInk(ink);
}

// src/buildPortalTheme.ts
var navByApp = {
  dealer: { sidebar: { light: "#203450", dark: "#162438" } },
  admin: {
    sidebar: { light: allTokens.palette.grounding.main, dark: allTokens.palette.grounding[700] },
    prod: allTokens.palette.grounding.main,
    test: "#666d80"
  }
};
var brand = {
  primary: { darker: "#802418", dark: "#cc3a26", main: allTokens.palette.primaryAccent.main, light: "#ff6d59", lighter: "#ffa498", contrastText: "#fff" },
  secondary: { darker: "#203450", dark: "#335380", main: allTokens.palette.secondaryAccent.main, light: "#6686b3", lighter: "#a0b4d0", contrastText: "#fff" },
  tertiary: { darker: "#205058", dark: "#33808d", main: allTokens.palette.tertiaryAccent.main, light: "#66b3c0", lighter: "#a0d0d8", contrastText: "#fff" },
  utility: { darker: "#202430", dark: "#333a4d", main: allTokens.palette.utility.main, light: "#666d80", lighter: "#a0a4b0", contrastText: "#fff" },
  // Semantic mains diverge from the token scale; dealer's customer-facing values win.
  error: { darker: "#801020", dark: "#cc1a33", main: "#ff2040", light: "#ff4d66", lighter: "#ff90a0", contrastText: "#fff" },
  warning: { darker: "#805018", dark: "#cc8026", main: "#ffa030", light: "#ffb359", lighter: "#ffd098", contrastText: "#fff" },
  success: { darker: "#185a24", dark: "#26903a", main: "#30b448", light: "#59c36d", lighter: "#98daa4", contrastText: "#fff" },
  info: { darker: "#185070", dark: "#2680b3", main: "#30a0e0", light: "#59b3e6", lighter: "#98d0f0", contrastText: "#fff" }
};
var grey = {
  50: "#f1f2f3",
  100: "#e4e6e7",
  150: "#d6d9db",
  200: "#c9cccf",
  250: "#bbc0c3",
  300: "#adb3b8",
  350: "#a0a6ac",
  400: "#9299a0",
  450: "#858d94",
  500: "#778088",
  550: "#6b737a",
  600: "#5f666d",
  650: "#535a5f",
  700: "#474d52",
  750: "#3c4044",
  800: "#303336",
  850: "#242629",
  900: "#181a1b",
  950: "#0c0d0e",
  A100: "#f1f2f3",
  A200: "#c9cccf",
  A400: "#9299a0",
  A700: "#474d52"
};
var productColors = {
  ops: { main: "#f05020", light: "#f37350", dark: "#c04019", contrastText: "#fff" },
  concierge: { main: "#4080c8", light: "#6699d3", dark: "#3366a0", contrastText: "#fff" },
  loaner: { main: "#2b5585", light: "#55779d", dark: "#22446a", contrastText: "#fff" },
  shuttle: { main: "#20a090", light: "#4db3a6", dark: "#198073", contrastText: "#fff" },
  auto: { main: "#20c880", light: "#4dd399", dark: "#19a066", contrastText: "#fff" },
  lyft: { main: "#ea0b8c", light: "#ee3ca3", dark: "#bb0970", contrastText: "#fff" },
  uber: { main: "#000000", light: "#333333", dark: "#000000", contrastText: "#fff" },
  android: { main: "#00de7a", light: "#33e595", dark: "#00b162", contrastText: "#fff" },
  ios: { main: "#b3b3b3", light: "#c2c2c2", dark: "#8f8f8f", contrastText: "#000" }
};
var orderStatus = {
  upcoming: brand.info.main,
  active: brand.warning.dark ?? brand.warning.main,
  completed: brand.success.main,
  cancelled: brand.error.main,
  unconfirmed: brand.warning.main
};
var heroSurface = {
  light: allTokens.surfaces.light.darkPanel,
  dark: allTokens.surfaces.dark.cardElevated
};
var buildHeroPalette = (background) => {
  const slots = readableContrastSlots(background) ?? contrastSlotsFromInk("#ffffff");
  return {
    background,
    backgroundElevated: lighten(background, 0.08),
    contrastText: slots.text.contrastPrimary,
    contrastSecondary: slots.text.contrastSecondary,
    divider: slots.dividerContrast,
    action: {
      hover: slots.action.contrastHover,
      selected: slots.action.contrastSelected,
      focus: slots.action.contrastFocus
    }
  };
};
var chart = {
  gridStroke: "#e1e3e5",
  axisTick: "#788088",
  identifier: "#4068a0",
  series: ["#4068a0", "#40a0b0", "#ff4830", "#f4b020", "#20b020", "#7b61ff", "#e06090", "#ff8a50"]
};
var interTight = `"Inter Tight", "Roboto", "Helvetica", "Arial", sans-serif`;
var floatingSurface = (theme) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 10,
  backgroundColor: alpha2(theme.palette.background.paper, 0.91),
  boxShadow: `0px 2px 16px 2px ${alpha2(theme.palette.common.black, 0.157)}`,
  backdropFilter: `blur(4px)`
});
var portalComponents = {
  MuiAlertTitle: { styleOverrides: { root: { margin: 0 } } },
  MuiAutocomplete: {
    styleOverrides: {
      listbox: { padding: 0 },
      loading: { padding: `4px 8px`, borderRadius: 6, backgroundColor: `transparent` },
      noOptions: { padding: `4px 8px`, borderRadius: 6, backgroundColor: `transparent` },
      option: {
        padding: `4px 8px`,
        borderRadius: 6,
        backgroundColor: `transparent`,
        marginBottom: 4,
        "&:last-child": { marginBottom: 0 }
      },
      paper: { backgroundColor: `transparent` },
      popper: ({ theme }) => ({ padding: 6, ...floatingSurface(theme) })
    }
  },
  MuiButton: {
    defaultProps: { disableElevation: true, variant: `contained` },
    styleOverrides: {
      root: ({ ownerState }) => ({
        ...ownerState.size === "small" && { height: 30 },
        ...ownerState.size === "medium" && { height: 36 },
        ...ownerState.size === "large" && { height: 42 }
      })
    }
  },
  MuiButtonGroup: { defaultProps: { disableElevation: true, variant: `contained` } },
  MuiCheckbox: {
    defaultProps: { color: `primary` },
    styleOverrides: { root: { borderRadius: 6, padding: 8 } }
  },
  MuiChip: { defaultProps: { size: `small` } },
  MuiDivider: { styleOverrides: { root: { padding: 0 } } },
  MuiIconButton: { styleOverrides: { root: { padding: 4 } } },
  MuiInput: { defaultProps: { size: `small` } },
  MuiMenu: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      list: { paddingTop: 6, paddingBottom: 6 },
      paper: ({ theme }) => floatingSurface(theme)
    }
  },
  MuiMenuItem: {
    defaultProps: { dense: true },
    styleOverrides: {
      root: {
        paddingLeft: 8,
        paddingRight: 8,
        borderRadius: 6,
        marginBottom: 4,
        marginLeft: 6,
        marginRight: 6,
        "&:last-child": { marginBottom: 0 }
      }
    }
  },
  MuiMenuList: { defaultProps: { dense: true } },
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        ...ownerState.variant === "custom" && {
          borderRadius: 10,
          boxShadow: `0px 2px 12px 2px ${alpha2(theme.palette.common.black, 0.094)}`
        },
        ...ownerState.variant === "customPrint" && {
          borderRadius: 10,
          border: `1px solid ${alpha2(theme.palette.text.primary, 0.31)}`
        }
      })
    }
  },
  MuiSelect: { defaultProps: { size: `small`, variant: `outlined` } },
  MuiTextField: { defaultProps: { size: `small`, variant: `outlined` } },
  MuiTooltip: { defaultProps: { arrow: true, enterDelay: 250, followCursor: true } },
  MuiTypography: {
    variants: [
      { props: { variant: `body3` }, style: { fontFamily: interTight, fontSize: `12px`, fontWeight: 400, lineHeight: 1.333 } },
      { props: { variant: `body4` }, style: { fontFamily: interTight, fontSize: `10px`, fontWeight: 400, lineHeight: 1.333 } },
      { props: { variant: `PageHeader` }, style: { fontFamily: interTight, fontSize: `24px`, fontWeight: 600, lineHeight: `1rem`, display: `inline`, verticalAlign: `top` } },
      { props: { variant: `SectionHeader` }, style: { fontFamily: interTight, fontSize: `21px`, fontWeight: 600, lineHeight: 1, cursor: `default` } }
    ]
  }
};
function buildPortalTheme(mode, options = {}) {
  const base = withoutBaseTableHead(toMUITheme(mode));
  const white = base.palette.common.white;
  const navApp = navByApp[options.app ?? "dealer"];
  const navExtras = "prod" in navApp ? { prod: navApp.prod, test: navApp.test } : {};
  const ink = contrastSlotsFromInk(white);
  const pageInk = contrastSlotsFromInk(base.palette.text.primary).text;
  const portal = createTheme2(base, {
    // Dealer's corner radius wins over the base's radius.md (8).
    shape: { borderRadius: allTokens.radius.sm },
    palette: {
      ...brand,
      grey,
      ...productColors,
      hero: buildHeroPalette(heroSurface[mode]),
      orderStatus,
      chart,
      // Stronger divider than the base's: dark ink on light, light ink on dark.
      dividerDark: mode === "light" ? "#00204028" : alpha2(white, 0.16),
      nav: {
        header: alpha2(base.palette.background.paper, 0.91),
        contrastText: ink.navContrastText,
        sidebar: navApp.sidebar[mode],
        ...navExtras
      },
      dividerContrast: ink.dividerContrast,
      action: ink.action,
      text: { primary: pageInk.contrastPrimary, secondary: pageInk.contrastSecondary, disabled: pageInk.contrastDisabled, ...ink.text },
      // Dealer's extra light surfaces (dealer's alt/alt2, admin's dark) in light mode;
      // the mode-correct dark equivalents from the design system's dark surfaces.
      background: mode === "light" ? { alt: "#f0f2f4", alt2: "#e8ebee", dark: "#e8eaec" } : {
        alt: allTokens.surfaces.dark.darkPanelElevated,
        alt2: allTokens.surfaces.dark.cardElevated,
        dark: allTokens.surfaces.dark.page
      }
    },
    // Dealer's type scale, with Inter Tight forced onto the variants the base pins
    // to Inter (headings, button) so the whole UI is one font. fontWeightBold is
    // dealer's 600, not MUI's 700.
    typography: {
      fontFamily: interTight,
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h1: { fontFamily: interTight, fontSize: `32px`, lineHeight: 1.5 },
      h2: { fontFamily: interTight, fontSize: `28px`, lineHeight: 1.5 },
      h3: { fontFamily: interTight, fontSize: `24px`, lineHeight: 1.5 },
      h4: { fontFamily: interTight, fontSize: `22px`, lineHeight: 1.5 },
      h5: { fontFamily: interTight, fontSize: `20px`, lineHeight: 1.5 },
      h6: { fontFamily: interTight, fontSize: `18px`, lineHeight: 1.5 },
      subtitle1: { fontSize: `16px`, lineHeight: 1.4 },
      subtitle2: { fontSize: `14px`, lineHeight: 1.4 },
      body1: { fontSize: `16px`, lineHeight: 1.333 },
      body2: { fontSize: `14px`, lineHeight: 1.333 },
      button: { fontFamily: interTight, fontSize: `14px`, fontWeight: 700, lineHeight: `14px`, textTransform: `none` },
      caption: { fontSize: `0.75rem`, lineHeight: 1.25 },
      overline: { fontSize: `0.625rem`, lineHeight: 1.25 }
    },
    components: portalComponents
  });
  const branding = resolveActiveBranding(options.branding);
  if (!branding) return portal;
  return createTheme2(portal, brandingOverrides(branding));
}
function withoutBaseTableHead(base) {
  const { MuiTableHead: _baseTableHead, ...components } = base.components ?? {};
  return { ...base, components };
}
function brandingOverrides(branding) {
  const palette = {};
  const primary = deriveBrandPaletteColor(branding.primary_color);
  if (primary) palette.primary = primary;
  const secondary = deriveBrandPaletteColor(branding.secondary_color);
  if (secondary) palette.secondary = secondary;
  const backdrop = branding.backdrop_color;
  const slots = readableContrastSlots(backdrop);
  if (slots && backdrop) {
    palette.nav = { sidebar: backdrop, contrastText: slots.navContrastText };
    palette.dividerContrast = slots.dividerContrast;
    palette.action = slots.action;
    palette.text = slots.text;
  }
  return { palette };
}

// src/pageLayout.ts
var PAGE_MAX_WIDTH = 1400;
var PAGE_PADDING = 3;
var PAGE_SX = { maxWidth: PAGE_MAX_WIDTH, mx: "auto", p: PAGE_PADDING };
export {
  PAGE_MAX_WIDTH,
  PAGE_PADDING,
  PAGE_SX,
  buildPortalTheme,
  resolveActiveBranding
};
//# sourceMappingURL=index.mjs.map